import { EvaluationColor } from '@repo/types';
import c from 'classnames';
import styles from './DivergingBar.module.css';
import { computeDivergingFill } from './utils';

type Props = {
  /** Signed value plotted on the [min, max] scale. */
  value: number;
  /** Fill color — the value's evaluation tier (from the DB), not its side. */
  color: EvaluationColor;
  /** Lower scale bound; values below are clamped to it. Defaults to -2. */
  min?: number;
  /** Upper scale bound; values above are clamped to it. Defaults to 2. */
  max?: number;
  /** Anchor the fill grows from. Defaults to 0. */
  baseline?: number;
  className?: string;
};

export function DivergingBar({
  value,
  color,
  min = -2,
  max = 2,
  baseline = 0,
  className,
}: Props) {
  const { left, width, valuePosition, baselinePosition, direction } =
    computeDivergingFill({
      value,
      min,
      max,
      baseline,
    });

  // Nudge the knob a hair inward (~the fill's cap radius) so it sits just
  // inside the rounded end instead of poking past it. The sign flips by side:
  // + moves right (younger/below), - moves left (older/above).
  const knobLeft = `calc(${valuePosition}% ${
    direction === 'above' ? '-' : '+'
  } 0.5rem)`;

  return (
    <div className={c(styles.track, className)}>
      {width > 0 && (
        <>
          <div
            className={c(styles.fill, styles[color], styles[direction])}
            style={{ left: `${left}%`, width: `${width}%` }}
          />
          <span
            className={styles.knob}
            style={{
              left: knobLeft,
            }}
          />
        </>
      )}
      <span
        className={styles.baseline}
        style={{ left: `${baselinePosition}%` }}
      />
    </div>
  );
}
